/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { LinearProgress, Popover } from '@mui/material';
import { IconCheck, IconEye, IconPlus, IconX } from '@tabler/icons-react';

import { AlertMessage, Button, Textarea, TableRowActions, CalendarItemStatusLabel, ImagesInput } from 'ui';
import { CALENDAR_ITEM_STATUS } from 'common/constants';

import { buildUploadedImageUrl, getImageUploadUrl, updateCalendar, uploadImage } from '../../api';
import SuggestEmailModal from './SuggestEmailModal';

const CalendarItemsTable = ({ calendar }) => {
  const [{ items, calendarId, status }, setCalendar] = useState(calendar);
  const [additionalInfo, setAdditionalInfo] = useState({});
  const [alternativeTopic, setAlternativeTopic] = useState({});
  const [images, setImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, action: null, item: null, index: null });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const activeSendTime = actionMenu.item?.sendTime;
  const isCalendarLocked = status === 'approved';

  const hasViewableFeedback = (item) => {
    return item?.additionalInfo || item?.alternativeTopic || item?.images?.length > 0;
  };

  const getRowActions = (item) => {
    if (isCalendarLocked) {
      return hasViewableFeedback(item) ? ['View Feedback'] : [];
    }
    return item.status === CALENDAR_ITEM_STATUS.suggested ? [] : ['Approve', 'Suggest Alternative'];
  };

  const setActiveImageErrors = (errors) => {
    if (!activeSendTime) return;
    setImageErrors((prev) => ({
      ...prev,
      [activeSendTime]: errors || [],
    }));
  };

  const completionPercentage =
    (items.reduce((acc, item) => {
      return acc + (item.status && item.status !== CALENDAR_ITEM_STATUS.pendingReview ? 1 : 0);
    }, 0) /
      items.length) *
    100;

  const getCompletionClassName = () => {
    if (completionPercentage >= 90) return '!bg-green-500';
    if (completionPercentage >= 50) return '!bg-orange-500';
    return '!bg-red-500';
  };

  const readImageMetadata = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => reject(new Error('Unable to read image dimensions'));
        img.src = reader.result;
      };

      reader.onerror = () => reject(reader.error || new Error('Unable to read file'));

      reader.readAsDataURL(file);
    });

  const handleSaveItem = async (index, itemStatus) => {
    try {
      let updatedItems = [...items];
      const sendTime = updatedItems[index].sendTime;
      updatedItems[index] = {
        ...updatedItems[index],
        status: itemStatus,
        additionalInfo:
          itemStatus === CALENDAR_ITEM_STATUS.approved
            ? additionalInfo[sendTime] || ''
            : updatedItems[index].additionalInfo,
        alternativeTopic:
          itemStatus === CALENDAR_ITEM_STATUS.needsRevision
            ? alternativeTopic[sendTime] || ''
            : updatedItems[index].alternativeTopic,
        images: images[sendTime] || [],
      };
      const updatedCalendar = await updateCalendar(calendarId, {
        items: updatedItems,
        status,
      });
      setCalendar(updatedCalendar);
      setSuccessMessage('Calendar item updated successfully!');
      setErrorMessage('');
    } catch (error) {
      console.error('Failed to update calendar item:', error);
      setErrorMessage('Failed to update calendar item. Please try again.');
    }
  };

  const handleSubmit = async () => {
    try {
      const updatedCalendar = await updateCalendar(calendarId, {
        items,
        status: 'approved',
      });
      setCalendar(updatedCalendar);
      setSuccessMessage('Calendar approved and submitted successfully!');
      setErrorMessage('');
    } catch (error) {
      console.error('Failed to update calendar:', error);
      setErrorMessage('Failed to approve calendar. Please try again.');
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'topic',
        header: () => 'Email Topic & Focus',
        footer: (props) => props.column.id,
        cell: (info) => (
          <div>
            <p className="text-base">{info.getValue()}</p>
            <p className="text-sm text-gray-600">{info.row.original.focus}</p>
          </div>
        ),
      },
      {
        accessorKey: 'sendTime',
        header: () => 'Send Date',
        footer: (props) => props.column.id,
        cell: (info) => {
          const value = info.getValue();
          return value ? format(new Date(value), 'MMM d, yyyy') : '-';
        },
        size: 120,
      },
      {
        accessorKey: 'type',
        header: () => 'Type',
        footer: (props) => props.column.id,
        cell: (info) => info.getValue() || '-',
        size: 150,
      },
      {
        accessorKey: 'status',
        header: () => 'Status',
        footer: (props) => props.column.id,
        cell: (info) => <CalendarItemStatusLabel status={info.getValue()} />,
        size: 145,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: items || [],
    columns,
    defaultColumn: {
      size: 'fit-content',
    },
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const populatedAdditionalInfo = {};
    const populatedAlternativeTopic = {};
    const populatedImages = {};

    (items || []).forEach((item) => {
      if (item?.sendTime && item?.additionalInfo) {
        populatedAdditionalInfo[item.sendTime] = item.additionalInfo;
      }

      if (item?.sendTime && item?.alternativeTopic) {
        populatedAlternativeTopic[item.sendTime] = item.alternativeTopic;
      }

      if (item?.sendTime && item?.images?.length) {
        populatedImages[item.sendTime] = item.images;
      }
    });

    setAdditionalInfo(populatedAdditionalInfo);
    setAlternativeTopic(populatedAlternativeTopic);
    setImages(populatedImages);
  }, [items]);

  const handleActionSelect = (action, item, index, event) => {
    if (action === 'Suggest Alternative') {
      setActionMenu({ anchorEl: event?.currentTarget ?? null, action, item, index });
    } else if (action === 'Approve') {
      setActionMenu({ anchorEl: event?.currentTarget ?? null, action, item, index });
    } else if (action === 'View Feedback') {
      setActionMenu({ anchorEl: event?.currentTarget ?? null, action, item, index });
    }
  };

  const handleCloseActionMenu = () => {
    setActionMenu({ anchorEl: null, action: null, item: null, index: null });
  };

  const handleAddNewItem = async (newItem) => {
    const parsedSendTime = new Date(newItem.sendTime);

    setIsAddingItem(true);

    try {
      const updatedCalendar = await updateCalendar(calendarId, {
        items: [
          ...(items || []),
          {
            topic: newItem.topic,
            focus: newItem.focus,
            type: newItem.type,
            sendTime: parsedSendTime.toISOString(),
            status: CALENDAR_ITEM_STATUS.suggested,
          },
        ],
        status,
      });
      setCalendar(updatedCalendar);
      setIsAddModalOpen(false);
      setSuccessMessage('Additional email suggested successfully!');
      setErrorMessage('');
    } catch (error) {
      console.error('Failed to add calendar item:', error);
      setErrorMessage('Failed to add additional email. Please try again.');
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleImagesEdit = async (files) => {
    if (!files.length) return;
    const sendTimeForUpload = activeSendTime;
    if (!sendTimeForUpload) return;

    setActiveImageErrors([]);

    try {
      const upload = await getImageUploadUrl();
      const { fields, url } = upload;
      const results = await Promise.allSettled(files.map(async (file) => uploadImage(url, fields, file)));

      const successfulUploads = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      const failedUploads = results.filter((result) => result.status === 'rejected');

      const uploadsWithMetadata = await Promise.all(
        successfulUploads.map(async (file) => {
          try {
            const { width, height } = await readImageMetadata(file);
            return {
              url: buildUploadedImageUrl(upload, file.name),
              width,
              height,
              filename: file.name,
              contentType: file.type,
              size: file.size,
            };
          } catch (err) {
            console.error('Failed to read image metadata:', err);
            return {
              url: buildUploadedImageUrl(upload, file.name),
              width: undefined,
              height: undefined,
              filename: file.name,
              contentType: file.type,
              size: file.size,
            };
          }
        }),
      );

      setImages((prevImages) => {
        return {
          ...prevImages,
          [sendTimeForUpload]: [...(prevImages[sendTimeForUpload] || []), ...uploadsWithMetadata],
        };
      });

      if (failedUploads.length) {
        setActiveImageErrors(['Some images failed to upload. Please try again.']);
        setErrorMessage('Some images failed to upload. Please try again.');
      } else {
        setErrorMessage('');
      }
    } catch (error) {
      setActiveImageErrors(['Failed to upload image. Please try again.']);
      setErrorMessage('Failed to upload image. Please try again.');
    }
  };

  const handleImageDelete = (url) => {
    const sendTimeForUpload = activeSendTime;
    if (!sendTimeForUpload) return;

    setActiveImageErrors([]);

    const updatedImages = (images[sendTimeForUpload] || []).filter((image) => image.url !== url);

    setImages((prevImages) => ({
      ...prevImages,
      [sendTimeForUpload]: updatedImages,
    }));
  };

  return (
    <div className="space-y-6">
      <div className="top-0 z-20 flex items-center justify-between rounded-lg border border-gray-100 bg-white-100 p-8">
        {status === 'approved' ? (
          <div className="space-y-4">
            <p className="max-w-3xl text-lg">Content calendar has been approved and submitted!</p>
            <p className="max-w-3xl text-lg">
              The DonorSpring team will use the topics, feedback, and additional information to create future Suggested
              Drafts.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="max-w-3xl text-lg">
              The DonorSpring team has prepared a draft calendar of potential email topics for your review and feedback.
              You have the following options for each item:
            </p>
            <ul className="space-y-1 text-base">
              <li className="flex items-center gap-2">
                <IconCheck size={18} className="text-gray-600" />
                <span>
                  <strong className="font-semibold">Approve:</strong> approve the topic and provide feedback or relevant
                  information that could improve the email.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <IconX size={18} className="text-gray-600" />
                <span>
                  <strong className="font-semibold">Suggest Alternative:</strong> request a different topic or approach.
                </span>
              </li>
            </ul>
            <p className="max-w-3xl text-lg">
              After reviewing all topics, click the &quot;Approve & Submit&quot; button to finalize your calendar. The
              topics and feedback will then be used to create future Suggested Drafts.
            </p>
          </div>
        )}
        <div className="flex flex-col items-end space-y-4">
          <div className="w-full">
            <div className="text-lg font-semibold">{Math.round(completionPercentage)}% Complete</div>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              classes={{
                root: '!bg-gray-50 rounded !h-1.5',
                bar: getCompletionClassName(),
              }}
            />
          </div>
          <Button
            onClick={handleSubmit}
            color="primary"
            title={status === 'approved' ? 'Approved' : 'Approve & Submit'}
            disabled={completionPercentage < 100 || status === 'approved'}
          />
        </div>
      </div>

      <div className="relative overflow-x-auto sm:rounded-lg">
        <table className="w-full text-left text-base text-gray-950 dark:text-gray-400">
          <thead className="bg-white-100 text-sm uppercase text-gray-400 dark:bg-gray-700 dark:text-gray-400">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="px-4 py-3"
                    style={{ width: header.column.columnDef.size, minWidth: header.column.columnDef.minSize }}
                  >
                    {header.isPlaceholder ? null : (
                      <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                    )}
                  </th>
                ))}
                <th className="px-4 py-3" />
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              const item = row.original;
              return (
                <tr key={row.id} className="border-b border-gray-50 bg-white-100 hover:bg-gray-10">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-3"
                      style={{ width: cell.column.columnDef.size, minWidth: cell.column.columnDef.minSize }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <TableRowActions
                      actions={getRowActions(item)}
                      onSelectAction={(action, event) => handleActionSelect(action, item, index, event)}
                      getActionIcon={(action) => {
                        switch (action) {
                          case 'Approve':
                            return (
                              <IconCheck
                                size={20}
                                className={`${item.status === CALENDAR_ITEM_STATUS.approved ? 'text-green-500' : ''}`}
                              />
                            );
                          case 'Suggest Alternative':
                            return (
                              <IconX
                                size={20}
                                className={`${item.status === CALENDAR_ITEM_STATUS.needsRevision ? 'text-orange-700' : ''}`}
                              />
                            );
                          case 'View Feedback':
                            return <IconEye size={20} className="text-gray-600" />;
                          default:
                            return null;
                        }
                      }}
                      visibleActions={3}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Button
        onClick={() => setIsAddModalOpen(true)}
        color="primary"
        title="Suggest Additional Email"
        LeftIcon={IconPlus}
        disabled={isCalendarLocked}
      />

      <SuggestEmailModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddNewItem}
        isSubmitting={isAddingItem}
      />

      <Popover
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={handleCloseActionMenu}
        classes={{ paper: '!rounded-lg !p-0' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        marginThreshold={32}
      >
        {actionMenu.action === 'Approve' && (
          <div className="w-[420px] space-y-3 px-4 py-3">
            <Textarea
              label="Feedback or Additional Information"
              value={additionalInfo[activeSendTime] || ''}
              onChange={(e) => setAdditionalInfo({ ...additionalInfo, [activeSendTime]: e.target.value })}
              rows={3}
              helperText="Quotes, statistics, or other information to help improve this email"
            />
            <ImagesInput
              name="Email Image"
              maxFiles={1}
              buttonTitle="Upload Supporting Image"
              onChange={handleImagesEdit}
              onDelete={handleImageDelete}
              errors={imageErrors[activeSendTime] || []}
              setErrors={(errors) => setActiveImageErrors(errors)}
              imageUrls={(images[activeSendTime] || []).map((image) => image.url)}
            />
            <div className="flex justify-end gap-2">
              <Button title="Cancel" onClick={handleCloseActionMenu} />
              <Button
                onClick={async () => {
                  await handleSaveItem(actionMenu.index, CALENDAR_ITEM_STATUS.approved);
                  handleCloseActionMenu();
                }}
                color="primary"
                className="ml-2"
                title="Confirm"
              />
            </div>
          </div>
        )}

        {actionMenu.action === 'Suggest Alternative' && (
          <div className="w-[420px] space-y-3 px-4 py-3">
            <Textarea
              label="Alternative Topic Suggestion"
              value={alternativeTopic[activeSendTime] || ''}
              onChange={(e) => setAlternativeTopic({ ...alternativeTopic, [activeSendTime]: e.target.value })}
              rows={3}
              helperText="Describe your alternative topic idea"
            />
            <div className="flex justify-end gap-2">
              <Button title="Cancel" onClick={handleCloseActionMenu} />
              <Button
                onClick={async () => {
                  await handleSaveItem(actionMenu.index, CALENDAR_ITEM_STATUS.needsRevision);
                  handleCloseActionMenu();
                }}
                color="primary"
                className="ml-2"
                title="Confirm"
              />
            </div>
          </div>
        )}

        {actionMenu.action === 'View Feedback' && (
          <div className="w-[420px] space-y-3 px-4 py-3">
            {actionMenu.item?.additionalInfo && (
              <Textarea label="Feedback Provided" value={actionMenu.item.additionalInfo} readOnly rows={3} />
            )}
            {actionMenu.item?.alternativeTopic && (
              <Textarea
                label="Alternative Topic Suggested"
                value={actionMenu.item.alternativeTopic}
                readOnly
                rows={3}
              />
            )}
            {actionMenu.item?.images?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Uploaded Images</p>
                <div className="flex flex-wrap gap-2">
                  {actionMenu.item.images.map((image) => (
                    <img
                      key={image.url}
                      src={image.url}
                      alt={image.filename || 'Supporting image'}
                      className="max-h-32 rounded-lg border border-gray-100 object-cover"
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button title="Close" onClick={handleCloseActionMenu} />
            </div>
          </div>
        )}
      </Popover>

      <AlertMessage
        open={!!successMessage}
        message={successMessage || 'Operation completed successfully!'}
        onClose={() => setSuccessMessage('')}
        severity="success"
      />

      <AlertMessage
        open={!!errorMessage}
        message={errorMessage || 'Oops, something went wrong!'}
        onClose={() => setErrorMessage('')}
        severity="error"
      />
    </div>
  );
};

CalendarItemsTable.propTypes = {
  calendar: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        topic: PropTypes.string,
        focus: PropTypes.string,
        sendTime: PropTypes.string,
        type: PropTypes.string,
        status: PropTypes.string,
      }),
    ),
    calendarId: PropTypes.string.isRequired,
    status: PropTypes.string,
  }),
};

CalendarItemsTable.defaultProps = {
  calendar: {
    items: [],
    calendarId: '',
    status: '',
  },
};

export default CalendarItemsTable;
